import {
  ExternalItemTypeDto,
  FeedItemDto,
  MemberTransferDataView,
  UserMemberStatusDto,
} from '@act/shared/data-transfer-objects';
import {
  PayloadAction,
  createAction,
  createEntityAdapter,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { feedRequests } from '../requests/feed.request';
import { createApiThunk } from '../utils/api-thunk';

import { selectMember, selectSelectedMemberId } from './selectedMemberSlice';

import {
  FeedItemDeletedEventPayload,
  FeedItemUpdatedEventPayload,
  FeedItemUpdatedTypes,
  UserMemberStatusDeletedWSPayload,
  UserMemberStatusEventTypes,
  UserMemberStatusWSPayload,
} from '@act/shared/socket-events';
import { addToEffectsMiddleware } from 'redux/utils/effects-middleware';
import {
  FeedFilter,
  FeedState,
  feedFiltersToEndpointFilters,
} from '../types/feedTypes';
import {
  auditMemberTransferRequestSuccess,
  createMemberTransferRequestSuccess,
} from './memberTransferRequestSlice';
import type { WebsocketEventActions } from './websocketSlice';
import { resetRedux } from '../actions/shared-actions';
import { useAppSelector } from '../store-hooks';

// THUNKS/ACTIONS ==============================================================
export const getFeedItems = createApiThunk(feedRequests.getFeedItems);
export const getPendingItems = createApiThunk(feedRequests.getPendingItems);
export type LoadFeedItemsPayload = { earlier?: boolean };
export const loadFeedItems = createAction<LoadFeedItemsPayload>(
  'feedItems/loadItems'
);
export type LoadPendingItemsPayload = { userId: string };
export const loadPendingItems = createAction<LoadPendingItemsPayload>(
  'pendingItems/loadItems'
);

// INITIAL STATE ===============================================================
const feedItemAdapter = createEntityAdapter<FeedItemDto>({
  sortComparer: (a: FeedItemDto, b: FeedItemDto) => {
    // Stable sort by timestamp, then by ID
    if (b.timestamp !== a.timestamp) {
      return b.timestamp - a.timestamp;
    }
    return b.id - a.id;
  },
});

const userMemberStatusAdapter = createEntityAdapter<UserMemberStatusDto>({
  sortComparer: (a: UserMemberStatusDto, b: UserMemberStatusDto) => {
    // Stable sort by effectiveAt, then by ID
    if (b.effectiveAt !== a.effectiveAt) {
      return b.effectiveAt - a.effectiveAt;
    }
    return b.id - a.id;
  },
});

export const initialState: FeedState = {
  status: 'initial',
  selectedMemberId: null, // Used for deduplication during loading
  displayedMemberId: null, // Used for websocket event filtering
  selectedFeedFilters: [],
  lastLoadedPendingItemsMemberId: null,

  feedItems: feedItemAdapter.getInitialState(),
  userMemberStatuses: userMemberStatusAdapter.getInitialState(),
};

// SLICE =======================================================================
export const feedSlice = createSlice({
  name: 'feed',
  initialState,
  reducers: {
    feedItemUpdated: (
      state: FeedState,
      action: PayloadAction<FeedItemUpdatedEventPayload>
    ) => {
      // make sure the websocket message is for the currently displayed member
      if (action.payload.userId === state.displayedMemberId) {
        //TODO: need to alert remaining redux states
        state.feedItems = feedItemAdapter.upsertMany(
          state.feedItems,
          action.payload.feedItemModelView.feedItems
        );
      }
    },
    feedItemDeleted: (
      state: FeedState,
      action: PayloadAction<FeedItemDeletedEventPayload>
    ) => {
      //TODO: need to alert remaining redux states
      feedItemAdapter.removeOne(state.feedItems, action.payload.feedItemId);
    },
    userMemberStatusCreated: (
      state: FeedState,
      action: PayloadAction<UserMemberStatusWSPayload>
    ) => {
      if (
        action.payload.userMemberStatus.memberId === state.displayedMemberId
      ) {
        userMemberStatusAdapter.addOne(
          state.userMemberStatuses,
          action.payload.userMemberStatus
        );
      }
    },
    userMemberStatusDeleted: (
      state: FeedState,
      action: PayloadAction<UserMemberStatusDeletedWSPayload>
    ) => {
      userMemberStatusAdapter.removeOne(
        state.userMemberStatuses,
        action.payload.userMemberStatusId
      );
    },
    selectFeedFilters: (
      state: FeedState,
      action: PayloadAction<FeedFilter[]>
    ) => {
      state.selectedFeedFilters = action.payload;
    },
    resetFeed: (state: FeedState) => {
      feedItemAdapter.removeAll(state.feedItems);
      userMemberStatusAdapter.removeAll(state.userMemberStatuses);
      state.lastLoadedPendingItemsMemberId = null;
    },
    setLoadingMember: (state: FeedState, action: PayloadAction<string>) => {
      state.selectedMemberId = action.payload;
    },
    setLoadingPendingItemsMember: (
      state: FeedState,
      action: PayloadAction<string>
    ) => {
      state.lastLoadedPendingItemsMemberId = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(resetRedux, state => {
        state = { ...initialState };
        return state;
      })
      .addCase(getFeedItems.pending, state => {
        state.status = 'loading';
      })
      .addCase(getFeedItems.fulfilled, (state, action) => {
        feedItemAdapter.addMany(state.feedItems, action.payload.feedItems);
        userMemberStatusAdapter.addMany(
          state.userMemberStatuses,
          action.payload.userMemberStatuses
        );
        state.status = 'success';
        // Clear the loading member ID so future loads aren't blocked
        state.selectedMemberId = null;
      })
      .addCase(getFeedItems.rejected, state => {
        state.status = 'failed';
        // Clear the loading member ID on error so retries aren't blocked
        state.selectedMemberId = null;
      })

      .addCase(selectMember, (state, action) => {
        state.feedItems = feedItemAdapter.removeAll(state.feedItems);
        state.userMemberStatuses = userMemberStatusAdapter.removeAll(
          state.userMemberStatuses
        );
        state.status = 'initial';
        // Set the displayed member ID for websocket event filtering
        state.displayedMemberId = action.payload;
        // Clear selectedMemberId so the next load isn't blocked
        state.selectedMemberId = null;
      })
      .addCase(
        createMemberTransferRequestSuccess,
        (state, action: PayloadAction<MemberTransferDataView>) => {
          feedItemAdapter.upsertOne(state.feedItems, action.payload.feedItem);
        }
      )
      .addCase(
        auditMemberTransferRequestSuccess,
        (state, action: PayloadAction<MemberTransferDataView>) => {
          feedItemAdapter.upsertOne(state.feedItems, action.payload.feedItem);
        }
      )

      // Listen for memberStatusChangeRequestCreated without importing the slice
      // NOTE: addMatcher must come AFTER all addCase calls
      .addMatcher(
        action => action.type === 'memberStatusChangeRequest/memberStatusChangeRequestCreated',
        (state, action: PayloadAction<{ feedItem?: FeedItemDto }>) => {
          if (!action.payload.feedItem) return;

          feedItemAdapter.upsertOne(state.feedItems, action.payload.feedItem);
        }
      );
  },
});

// REDUCER =====================================================================
export const feedReducer = feedSlice.reducer;

// SELECTORS ===================================================================
// FEED ITEMS -------------------------------
const selectFeedItemsState = state =>
  (state as { feed: typeof initialState })?.feed.feedItems;
const selectFeedState = state => (state as { feed: typeof initialState })?.feed;

export const { selectAll: selectFeedItems, selectIds: selectFeedItemIds } =
  feedItemAdapter.getSelectors(selectFeedItemsState);

export const selectFeedLoadingStatus = createSelector(
  selectFeedState,
  feedState => feedState.status
);

export const selectFeedItemByFeedItemId = (
  externalItemId: number,
  externalItemType: ExternalItemTypeDto
) =>
  createSelector(selectFeedItems, feedItems =>
    feedItems.find(
      feedItem =>
        feedItem.externalItemId === externalItemId &&
        feedItem.externalItemType === externalItemType
    )
  );

export const selectFeedFilters = feedSlice.actions.selectFeedFilters;

export const selectSelectedFeedFilters = createSelector(
  selectFeedState,
  feedState => feedState.selectedFeedFilters
);

// MEMBER STATUSES -------------------------------
const selectUserMemberStatusesState = state =>
  (state as { feed: typeof initialState })?.feed.userMemberStatuses;

export const { selectAll: selectUserMemberStatuses } =
  userMemberStatusAdapter.getSelectors(selectUserMemberStatusesState);

// select feed items and user member statuses and sort them by timestamp
export const selectFeedItemsAndUserMemberStatuses = createSelector(
  selectFeedItems,
  selectUserMemberStatuses,
  (feedItems, userMemberStatuses) => {
    const feedItemsArray = feedItems.map(feedItem => ({
      ...feedItem,
      type: feedItem.externalItemType,
    }));
    const userMemberStatusesArray = userMemberStatuses.map(
      userMemberStatus => ({
        ...userMemberStatus,
        type: 'member_status',
      })
    );
    const allItems: EventItem[] = [
      ...userMemberStatusesArray.map(u => {
        return {
          timestamp: u.effectiveAt,
          type: 'userMemberStatus' as const,
          item: u,
        };
      }),
      ...feedItemsArray.map(f => {
        return {
          timestamp: f.timestamp,
          type: 'feedItem' as const,
          item: f,
        };
      }),
    ];

    return allItems.sort((a, b) => {
      // Adding 1000ms to userMemberStatuses to make sure they are always
      // sorted after feedItems with the same timestamp.
      let compareA = a.timestamp;
      let compareB = b.timestamp;
      if (a.type === 'userMemberStatus') {
        compareA = a.timestamp + 1000;
      }
      if (b.type === 'userMemberStatus') {
        compareB = b.timestamp + 1000;
      }

      // FIXED: Stable sort - use subtraction for primary comparison
      if (compareB !== compareA) {
        return compareB - compareA;
      }

      // Fallback to stable secondary sort by type
      if (a.type !== b.type) {
        return a.type === 'feedItem' ? -1 : 1;
      }

      // Final fallback: sort by ID for complete stability
      const aId =
        a.type === 'feedItem'
          ? (a.item as FeedItemDto).id
          : (a.item as UserMemberStatusDto).id;
      const bId =
        b.type === 'feedItem'
          ? (b.item as FeedItemDto).id
          : (b.item as UserMemberStatusDto).id;
      return bId - aId;
    });
  }
);

// EXPORT HELPER ===============================================================
export const feedSelectors = {
  useSelectFeedItems: () => useAppSelector(selectFeedItems),
  useSelectFeedItemsAndUserMemberStatuses: () =>
    useAppSelector(selectFeedItemsAndUserMemberStatuses),
};

// EFFECTS =====================================================================
addToEffectsMiddleware(addEffect => {
  addEffect({
    action: loadFeedItems,
    effect: async ({ dispatch, action, state }) => {
      const payload = action.payload as LoadFeedItemsPayload;
      const selectedMemberId = selectSelectedMemberId(state);
      const feedState = selectFeedState(state);

      // Deduplicate: Don't reload if we're already loading this member's feed
      // (unless we're paginating with "earlier")
      if (!payload.earlier && feedState.selectedMemberId === selectedMemberId) {
        return;
      }

      let afterItemId: number = null;
      const filters = feedFiltersToEndpointFilters(
        feedState.selectedFeedFilters
      );
      if (payload.earlier) {
        const ids = selectFeedItemIds(state);
        afterItemId = ids[ids.length - 1];
      } else {
        void dispatch(feedSlice.actions.resetFeed());
        // Track which member we're loading feed for to prevent duplicates
        void dispatch(feedSlice.actions.setLoadingMember(selectedMemberId));
      }
      if (selectedMemberId) {
        dispatch(
          getFeedItems({
            params: {
              userId: selectedMemberId,
              'page-size': 100,
              'sort-direction': 'DESC',
              'after-item-id': afterItemId,
              type: filters.length > 0 ? filters.join(',') : null,
            },
          })
        );
      }
    },
  });
  addEffect({
    action: selectFeedFilters,
    effect: async ({ dispatch, listenerApi, state }) => {
      const oldRootState = await listenerApi.getOriginalState();
      const oldState = selectFeedState(oldRootState);
      const newState = selectFeedState(state);

      const bothEmpty =
        !oldState.selectedFeedFilters.length &&
        !newState.selectedFeedFilters.length;
      const filtersChanged =
        oldState.selectedFeedFilters !== newState.selectedFeedFilters &&
        !bothEmpty;

      // If the filters change then reload feed items
      if (filtersChanged) {
        dispatch(loadFeedItems({}));
      }
    },
  });
  addEffect({
    action: loadPendingItems,
    effect: async ({ dispatch, action, state }) => {
      const payload = action.payload as LoadPendingItemsPayload;
      const feedState = selectFeedState(state);

      // Deduplicate: Don't reload if we've already loaded this member's pending items
      if (feedState.lastLoadedPendingItemsMemberId === payload.userId) {
        return;
      }

      // Track which member we're loading pending items for to prevent duplicates
      void dispatch(
        feedSlice.actions.setLoadingPendingItemsMember(payload.userId)
      );

      dispatch(
        getPendingItems({
          params: {
            userId: payload.userId,
          },
        })
      );
    },
  });
});

// WEBSOCKET ACTIONS ===========================================================
export const feedSliceWebsocketActions: WebsocketEventActions[] = [
  {
    type: FeedItemUpdatedTypes.FEED_ITEM_UPDATED,
    action: feedSlice.actions.feedItemUpdated,
    channel: 'baseSocket',
  },
  {
    type: FeedItemUpdatedTypes.FEED_ITEM_DELETED,
    action: feedSlice.actions.feedItemDeleted,
    channel: 'baseSocket',
  },
  {
    type: UserMemberStatusEventTypes.USER_MEMBER_STATUS_CREATED,
    action: feedSlice.actions.userMemberStatusCreated,
    channel: 'baseSocket',
  },
  {
    type: UserMemberStatusEventTypes.USER_MEMBER_STATUS_DELETED,
    action: feedSlice.actions.userMemberStatusDeleted,
    channel: 'baseSocket',
  },
];

export interface EventItem {
  timestamp: number;
  type: 'feedItem' | 'userMemberStatus';
  item: FeedItemDto | UserMemberStatusDto;
}
